/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, ReactElement } from 'react';
import { useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import type { TaskItem as OriginalTaskItem } from '@atlaskit/renderer/nodes';
import { messages } from '@atlaskit/task-decision/messages';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

const styles = cssMap({
	taskItem: {
		display: 'flex',
		flexDirection: 'row',
		position: 'relative',
		minWidth: '48px',
	},
	checkboxSlot: {
		alignSelf: 'start',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexBasis: '24px',
		flexGrow: 0,
		flexShrink: 0,
		height: '24px',
		width: '24px',
	},
	checkbox: {
		height: '16px',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		width: '16px',
	},
	content: {
		flexBasis: 'auto',
		flexGrow: 1,
		flexShrink: 1,
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		minWidth: 0,
		wordWrap: 'break-word',
	},
});

export const TaskItem = (
	props: ComponentProps<typeof OriginalTaskItem> & LightNodeProps,
): ReactElement => {
	const intl = useIntl();
	const isDone = props.state === 'DONE';

	return (
		<div
			ref={props.lightNodeRef}
			css={styles.taskItem}
			data-task-local-id={props.localId}
			data-task-state={props.state}
		>
			<span css={styles.checkboxSlot} contentEditable={false}>
				{/* eslint-disable-next-line @atlaskit/design-system/no-html-checkbox -- Light rendering needs inert native task semantics without loading the interactive ADS checkbox. */}
				<input
					aria-label={intl.formatMessage(
						isDone ? messages.markTaskAsNotCompleted : messages.markTaskAsCompleted,
					)}
					css={styles.checkbox}
					type="checkbox"
					checked={isDone}
					disabled
				/>
			</span>
			<div css={styles.content}>{props.children}</div>
		</div>
	);
};
